import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form } from "react-bootstrap";
import {
  Monitor, Plus, RefreshCw, Trash2, RotateCcw, Search,
  Circle, AlertCircle, Ban, Zap,
} from "lucide-react";
import { PageHeader, AppButton, ConfirmModal, StatusBadge } from "../../ui";
import apiClient from "../../../utils/apiClient";
import "./zoom-hosts.scss";

const STATUS_MAP = {
  available: { label: "Available", variant: "success" },
  busy:      { label: "In Meeting", variant: "warning" },
  disabled:  { label: "Disabled",   variant: "secondary" },
};

const ZoomHostsPage = () => {
  const [hosts, setHosts] = useState([]);
  const [summary, setSummary] = useState({ total: 0, available: 0, busy: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Add / Edit modal
  const [showForm, setShowForm] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);

  // ── Fetch ──
  const fetchHosts = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get("/sa/zoom-hosts");
      setHosts(data.hosts || []);
      setSummary(data.summary || { total: 0, available: 0, busy: 0, disabled: 0 });
    } catch { /* keep empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHosts(); }, [fetchHosts]);

  // ── Add Host ──
  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!formEmail.trim()) { setFormError("Email is required."); return; }
    try {
      setFormLoading(true);
      await apiClient.post("/sa/zoom-hosts", {
        email: formEmail.trim(),
        displayName: formDisplayName.trim(),
      });
      setShowForm(false);
      setFormEmail("");
      setFormDisplayName("");
      await fetchHosts();
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to add host.");
    } finally { setFormLoading(false); }
  };

  // ── Toggle disable/enable ──
  const handleToggleStatus = async (host) => {
    const newStatus = host.status === "disabled" ? "available" : "disabled";
    try {
      await apiClient.put(`/sa/zoom-hosts/${host._id}`, { status: newStatus });
      await fetchHosts();
    } catch { /* ignore */ }
  };

  // ── Delete ──
  const handleDelete = async () => {
    try {
      await apiClient.delete(`/sa/zoom-hosts/${deleteId}`);
      setDeleteId(null);
      await fetchHosts();
    } catch { /* ignore */ }
  };

  // ── Force Release ──
  const handleForceRelease = async (hostId) => {
    try {
      await apiClient.post(`/sa/zoom-hosts/${hostId}/release`);
      await fetchHosts();
    } catch { /* ignore */ }
  };

  // ── Mark Busy (testing helper) ──
  const handleMarkBusy = async (hostId) => {
    try {
      await apiClient.put(`/sa/zoom-hosts/${hostId}`, { status: "busy" });
      await fetchHosts();
    } catch { /* ignore */ }
  };

  // ── Filter ──
  const filtered = hosts.filter((h) =>
    !search ||
    h.email.toLowerCase().includes(search.toLowerCase()) ||
    (h.displayName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="sa-zoom-hosts">
      <PageHeader
        title="Zoom Host Pool"
        description="Manage Zoom licensed users shared across institutes using Platform Hosted mode"
        icon={Monitor}
        actions={
          <AppButton
            variant="default"
            size="sm"
            onClick={() => { setFormEmail(""); setFormDisplayName(""); setFormError(""); setShowForm(true); }}
          >
            <Plus size={16} /> Add Host
          </AppButton>
        }
      />

      {/* ── Stats ── */}
      <div className="sa-zoom-hosts__stats">
        <div className="sa-zoom-hosts__stat">
          <span className="sa-zoom-hosts__stat-value">{summary.total}</span>
          <span className="sa-zoom-hosts__stat-label">Total Hosts</span>
        </div>
        <div className="sa-zoom-hosts__stat sa-zoom-hosts__stat--available">
          <span className="sa-zoom-hosts__stat-value">{summary.available}</span>
          <span className="sa-zoom-hosts__stat-label">Available</span>
        </div>
        <div className="sa-zoom-hosts__stat sa-zoom-hosts__stat--busy">
          <span className="sa-zoom-hosts__stat-value">{summary.busy}</span>
          <span className="sa-zoom-hosts__stat-label">In Meeting</span>
        </div>
        <div className="sa-zoom-hosts__stat sa-zoom-hosts__stat--disabled">
          <span className="sa-zoom-hosts__stat-value">{summary.disabled}</span>
          <span className="sa-zoom-hosts__stat-label">Disabled</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="sa-zoom-hosts__toolbar">
        <div className="sa-zoom-hosts__search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search hosts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <AppButton variant="ghost" size="sm" onClick={fetchHosts} disabled={loading}>
          <RefreshCw size={16} className={loading ? "spin" : ""} /> Refresh
        </AppButton>
      </div>

      {/* ── Hosts Table ── */}
      {filtered.length === 0 && !loading ? (
        <div className="sa-zoom-hosts__empty">
          <Monitor size={40} strokeWidth={1.5} />
          <h3>No hosts found</h3>
          <p>{search ? "Try adjusting your search." : "Add your first Zoom host to the pool."}</p>
        </div>
      ) : (
        <div className="sa-zoom-hosts__table-wrap">
          <table className="sa-zoom-hosts__table">
            <thead>
              <tr>
                <th>Host Email</th>
                <th>Display Name</th>
                <th>Status</th>
                <th>Current Assignment</th>
                <th>Meetings Hosted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((host) => {
                const st = STATUS_MAP[host.status] || STATUS_MAP.available;
                return (
                  <tr key={host._id} className={host.status === "disabled" ? "sa-zoom-hosts__row--disabled" : ""}>
                    <td className="sa-zoom-hosts__email">{host.email}</td>
                    <td>{host.displayName || "—"}</td>
                    <td>
                      <StatusBadge variant={st.variant}>{st.label}</StatusBadge>
                    </td>
                    <td>
                      {host.status === "available" && (
                        <button
                          className="sa-zoom-hosts__action-btn sa-zoom-hosts__action-btn--busy"
                          title="Mark Busy (testing)"
                          onClick={() => handleMarkBusy(host._id)}
                        >
                          <Zap size={15} />
                        </button>
                      )}
                      {host.status === "busy" ? (
                        <span className="sa-zoom-hosts__assignment">
                          {host.currentBatchId?.name || host.currentBatchId || "Unknown batch"}
                        </span>
                      ) : "—"}
                    </td>
                    <td>{host.meetingCount || 0}</td>
                    <td className="sa-zoom-hosts__actions">
                      {host.status === "busy" && (
                        <button
                          className="sa-zoom-hosts__action-btn sa-zoom-hosts__action-btn--release"
                          title="Force Release"
                          onClick={() => handleForceRelease(host._id)}
                        >
                          <RotateCcw size={15} />
                        </button>
                      )}
                      <button
                        className="sa-zoom-hosts__action-btn"
                        title={host.status === "disabled" ? "Enable" : "Disable"}
                        onClick={() => handleToggleStatus(host)}
                        disabled={host.status === "busy"}
                      >
                        {host.status === "disabled" ? <Circle size={15} /> : <Ban size={15} />}
                      </button>
                      <button
                        className="sa-zoom-hosts__action-btn sa-zoom-hosts__action-btn--danger"
                        title="Delete"
                        onClick={() => setDeleteId(host._id)}
                        disabled={host.status === "busy"}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add Host Modal ── */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Zoom Host</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdd}>
          <Modal.Body>
            {formError && (
              <div className="sa-zoom-hosts__form-error">
                <AlertCircle size={16} /> {formError}
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                placeholder="host@company.com"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                required
              />
              <Form.Text className="text-muted">
                Must be a licensed user in your Zoom account.
              </Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Classroom Host 1"
                value={formDisplayName}
                onChange={(e) => setFormDisplayName(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <AppButton variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </AppButton>
            <AppButton variant="default" size="sm" type="submit" disabled={formLoading}>
              {formLoading ? "Adding…" : "Add Host"}
            </AppButton>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Delete Confirm ── */}
      <ConfirmModal
        show={!!deleteId}
        onHide={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Remove Host"
        message="Are you sure you want to remove this host from the pool?"
        confirmText="Delete"
      />
    </div>
  );
};

export default ZoomHostsPage;
